import { Box, Icon, IconButton, Theme, Typography } from "@outschool/backpack";
import { faBars, faChevronLeft } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { LearnerRoutes } from "@outschool/routes";
import { dayjs, OUTSCHOOL_TIMEZONE } from "@outschool/time";
import { ReadOnlyBanner } from "@outschool/ui-components-website";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import MessageTeacherButton from "../../components/MessageTeacherButton";
import * as Env from "../../Env";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import { useLearnerClassroomContext } from "../../providers/LearnerClassroomProvider";
import { useLessonsNavDrawer } from "../../providers/LessonsNavDrawerProvider";

export default function ClassroomHeaderV2() {
  const Link = useLinkComponent();
  const { t } = useTranslation("learnerApp\\LearnerClassroom");
  const currentLearner = useCurrentLearner();
  const { openNavDrawer } = useLessonsNavDrawer();
  const { loading, error, activity, section } = useLearnerClassroomContext();

  if (loading || error || !activity || !section) {
    return null;
  }

  const userTimeZone = currentLearner?.timeZone || OUTSCHOOL_TIMEZONE;
  const endTime = section.end_time
    ? dayjs(section.end_time).tz(userTimeZone)
    : null;

  return (
    <Box
      data-test-id="class-header-v2"
      sx={(theme: Theme) => ({
        paddingLeft: "7.2rem",
        paddingRight: "0.8rem",
        paddingY: "2.2rem",
        [theme.breakpoints.down("md")]: {
          padding: 0,
        },
      })}
    >
      {Env.IS_READ_ONLY_MODE && <ReadOnlyBanner />}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.8rem",
          color: "grey.900",
        }}
      >
        <Link
          to={LearnerRoutes.dashboardPath()}
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "inherit",
            marginBottom: 7,
          }}
        >
          <Icon icon={faChevronLeft} sx={{ fontSize: "1.4rem" }} />
          <Typography variant="subtitle2" sx={{ letterSpacing: "0.1px" }}>
            {t("Back to all classes")}
          </Typography>
        </Link>

        <Box
          sx={(theme: Theme) => ({
            display: "block",
            [theme.breakpoints.up("md")]: {
              display: "none",
            },
          })}
        >
          <IconButton
            icon={faBars}
            variant="text"
            onClick={openNavDrawer}
            aria-label={t("Open lessons menu")}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.8rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.7rem",
            minWidth: 0,
          }}
        >
          <Typography
            variant="h2"
            sx={(theme: Theme) => ({
              letterSpacing: "-0.4px",
              color: "grey.900",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "3.2rem",
              [theme.breakpoints.up("md")]: {
                fontSize: "3.2rem",
              },
            })}
          >
            {activity.title}
          </Typography>

          {endTime && (
            <Typography
              variant="body1"
              sx={(theme: Theme) => ({
                marginBottom: "1rem",
                color: "grey.600",
                [theme.breakpoints.down("md")]: {
                  display: "none",
                },
              })}
            >
              {t("Class access ends {{date}}", {
                date: endTime.format("MMM D, YYYY"),
              })}
            </Typography>
          )}
        </Box>

        <MessageTeacherButton sectionUid={section.uid} />
      </Box>
    </Box>
  );
}
